import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import DraggableHierarchyItem, { DraggableHierarchyItemHandle } from '../DraggableHierarchyItem';
import { MermaidHierarchyItem, HierarchyPosition } from './types';

export interface MermaidNodeRendererProps {
  item: MermaidHierarchyItem;
  position: HierarchyPosition;
  onPositionChange: (position: HierarchyPosition) => void;
  zoomLevel?: number;
  dragEnabled?: boolean;
}

/**
 * Default renderer for Mermaid diagram nodes
 * Supports various node shapes and theme-aware styling
 */
const MermaidNodeRenderer: React.FC<MermaidNodeRendererProps> = ({
  item,
  position,
  onPositionChange,
  zoomLevel = 1,
  dragEnabled = true,
}) => {
  const theme = themeSelector();
  const nodeRef = React.useRef<DraggableHierarchyItemHandle>(null);

  // Calculate node dimensions based on shape and content
  const { width, height } = useMemo(() => {
    const baseWidth = 160;
    const baseHeight = 80;
    const labelLines = item.label.split('\n').length;
    const minHeight = Math.max(baseHeight, labelLines * 24 + 32);

    // Adjust for different shapes
    switch (item.shape) {
      case 'circle':
        return { width: Math.max(baseWidth, baseHeight), height: Math.max(baseWidth, baseHeight) };
      case 'diamond':
        return { width: baseWidth + 20, height: baseHeight + 20 };
      default:
        return { width: baseWidth, height: minHeight };
    }
  }, [item.shape, item.label]);

  // Get shape-specific styles
  const shapeStyles = useMemo(() => {
    const baseStyle: any = {
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 12,
    };

    const backgroundColor = theme ? '#FFFFFF' : '#1E1E1E';
    const borderColor = theme ? '#E0E0E0' : '#404040';

    switch (item.shape) {
      case 'circle':
        return {
          ...baseStyle,
          borderRadius: 9999, // Full circle
          backgroundColor,
          borderWidth: 2,
          borderColor,
        };
      case 'rounded':
        return {
          ...baseStyle,
          borderRadius: 20,
          backgroundColor,
          borderWidth: 2,
          borderColor,
        };
      case 'diamond':
        // Diamond shape using transform (simplified - could use SVG for perfect diamond)
        return {
          ...baseStyle,
          borderRadius: 8,
          backgroundColor,
          borderWidth: 2,
          borderColor,
        };
      case 'subroutine':
        return {
          ...baseStyle,
          borderRadius: 8,
          backgroundColor,
          borderWidth: 3,
          borderColor,
        };
      case 'parallelogram':
      case 'parallelogram-alt':
        return {
          ...baseStyle,
          borderRadius: 4,
          backgroundColor,
          borderWidth: 2,
          borderColor,
        };
      default: // rectangle
        return {
          ...baseStyle,
          borderRadius: 8,
          backgroundColor,
          borderWidth: 2,
          borderColor,
        };
    }
  }, [item.shape, theme]);

  // Render node content
  const renderNodeContent = () => {
    const textColor = theme ? '#000000' : '#FFFFFF';
    const labelLines = item.label.split('\n');

    return (
      <View style={styles.contentContainer}>
        {labelLines.map((line, index) => (
          <Text
            key={index}
            style={[
              styles.labelText,
              { color: textColor },
              labelLines.length > 1 && index === 0 && styles.titleText,
            ]}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {line}
          </Text>
        ))}
        {item.mermaidType && (
          <Text style={[styles.typeText, { color: textColor }]}>
            {item.mermaidType}
          </Text>
        )}
      </View>
    );
  };

  return (
    <DraggableHierarchyItem
      ref={nodeRef}
      item={item}
      initialPosition={position}
      onPositionChange={onPositionChange}
      dragEnabled={dragEnabled}
      zoomLevel={zoomLevel}
      itemWidth={width}
      itemHeight={height}
    >
      {({ isDragging }) => (
        <View
          style={[
            styles.nodeContainer,
            shapeStyles,
            isDragging && styles.dragging,
            item.style && { opacity: 0.9 }, // Apply custom style hint
          ]}
        >
          {renderNodeContent()}
        </View>
      )}
    </DraggableHierarchyItem>
  );
};

const styles = StyleSheet.create({
  nodeContainer: {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  labelText: {
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    marginVertical: 2,
  },
  titleText: {
    fontSize: 16,
    fontWeight: '600',
  },
  typeText: {
    fontSize: 10,
    marginTop: 4,
    opacity: 0.7,
  },
  dragging: {
    opacity: 0.8,
    shadowOpacity: 0.3,
    elevation: 8,
  },
});

export default MermaidNodeRenderer;

